import React, { useContext, useState } from 'react';
import {
  oneOfType, array, bool, func, any, string, object,
} from 'prop-types';
import { Collapse } from 'antd';

import { ChevronSVG } from 'components/iconsSVG/chevron';
import { i18nContext } from 'context/i18n';
import { dataLayerHandleEvent } from 'utils/dataLayers';
import { getLang, getMarket } from 'utils/helpers';
import { storeStatus } from 'utils/shopLocator';

import styles from './style.module.css';

const { Panel } = Collapse;

export const StoreTypeLabel = ({ store, t }) => {
  const { tipologia } = store;
  const storeTypes = {
    'FRANCHISE': {
      label: t('tiendas.locator', 'franchise.store', 'Franchise Store'),
      logo: '/assets-new/img/shops/franchise-bridge.png',
      className: styles.franchiseStore,
    },
    'TIENDA CAMPER': {
      label: t('tiendas.locator', 'camper.store', 'Camper Store'),
      logo: '/assets-new/img/shops/camper-bridge.png',
      className: styles.camperStore,
    },
    'ESPACIO CAMPER': {
      label: t('tiendas.locator', 'department.store', 'Department Store'),
      logo: '/assets-new/img/shops/franchise-bridge.png',
      className: styles.franchiseStore,
    },
    'RECAMPER': {
      label: t('tiendas.locator', 'outlet.store', 'Outlet Store'),
      logo: '/assets-new/img/shops/outlet-bridge.png',
      className: styles.outletStore,
    },
  };
  let storeType = null;
  if (tipologia in storeTypes) {
    storeType = storeTypes[tipologia];
  } else {
    storeType = storeTypes['TIENDA CAMPER'];
  }
  return <p className={storeType.className}><img className={styles.storeLogo} src={storeType.logo} alt="store logo"/> {storeType.label}</p>;
};

StoreTypeLabel.propTypes = {
  store: object.isRequired,
  t: func.isRequired,
};

const daysMapper = {
  0: 8, // Sunday
  1: 2, // Monday
  2: 3, // Tuesday
  3: 4, // Wednesday
  4: 5, // Thursday
  5: 6, // Friday
  6: 7, // Saturday
};
export const Scheduler = ({ schedule, storeName, storeId }) => {
  const [ isOpen, setIsOpen ] = useState(false);
  const handleOnChange = (panelProps) => {
    setIsOpen(panelProps && panelProps.length > 0);
  };
  let headerIndex = 0;
  if (!isOpen) {
    const date = new Date();
    const dayOfWeek = date.getDay();
    const dayOfWeekMapped = daysMapper[dayOfWeek];
    headerIndex = schedule.findIndex((day) => day.dayOfWeek === dayOfWeekMapped);
  }
  const header = <div className={styles.accordionHeader}>
    <span>{schedule[headerIndex].days}</span>
    {schedule[headerIndex].timetable.includes('/') ?
      <span>{schedule[headerIndex].timetable.split('/')[0]}<br/>{schedule[headerIndex].timetable.split('/')[1]}</span> :
      <span>{schedule[headerIndex].timetable}</span>
    }
  </div>;
  return (
    <Collapse
      bordered={false}
      expandIconPosition="right"
      expandIcon={({ isActive }) => {
        if (isActive) {
          dataLayerHandleEvent({
            event: 'store_calendar',
            event_category: 'store',
            event_detail_1: storeId,
            event_detail_2: storeName.toLowerCase(),
          });
        }
        return (<span className={isActive ? styles.chevronActive : ''}><ChevronSVG /></span>);
      }}
      className={styles.scheduleAccordion}
      ghost
      onChange={(panelProps) => handleOnChange(panelProps)}
    >
      <Panel header={header} key="1">
        <>
          {schedule.slice(1).map((item) => (
            <div key={item.days}>
              <span>{item.days}</span>
              {item.timetable.includes('/') ?
                <span>{item.timetable.split('/')[0]}<br/>{item.timetable.split('/')[1]}</span> :
                <span>{item.timetable}</span>
              }
            </div>
          ))}
        </>
      </Panel>
    </Collapse>
  );
};
Scheduler.propTypes = {
  schedule: array.isRequired,
  storeName: string,
  storeId: string,
};

export const StoresGrid = ({
  stores, withMap, selector, coloredStore, onMouseOverStore, onMouseOutStore, selectedStoreId, onClickStoreHref,
}) => {
  const { t, locale } = useContext(i18nContext);
  const lang = getLang(locale);
  const market = getMarket(locale);

  return (
    <div className={`${styles.storesList} ${withMap ? styles.wrapped : ''}`}>
      { selector !== null && selector }
      { stores.length === 0 && <p className={styles.notFoundMessage}>{t('modal.idiomas', 'no.matches', 'No matches found')}</p>}
      {
        stores.map((item, index) => <div onMouseEnter={() => onMouseOverStore(item)} onMouseLeave={onMouseOutStore} className={`${styles.storeCard} ${coloredStore === item.id ? styles.coloredCard : ''} ${selectedStoreId === item.id ? styles.selectedStore : ''}`} id={coloredStore === item.id ? 'selectedStore' : ''} key={`store-${index}`}>
          <StoreTypeLabel store={item} t={t} />
          <div className={styles.cardName}>
            <a href={`/${lang}_${market}/shops${item.url}`}>{item.name}</a>
          </div>
          <div className={styles.cardAddress}>
            <span className={styles.address}>{`${item.address1}, ${item.postalCode}, ${item.regionName}, ${item.countryCode}`}</span>
          </div>
          {item.status && item.statusText ? storeStatus(item.status, item.statusText) : null}
          {item.storeHours?.length && <div className={styles.storeSchedule}>
              <Scheduler schedule={item.storeHours} storeName={item.name} storeId={item.id} />
            </div>
          }
          {item.urlGoogleMaps &&
            <>
              <a className={styles.urlGoogleMaps} href={item.urlGoogleMaps} rel="noreferrer" target="_blank">
                {t('tiendas.locator', 'get.diretions', 'Get directions')}
                <img src="/assets-new/img/shops/chevron-right.png" alt="chevron-right" />
              </a>
              <br></br>
            </>
          }
          <a onClick={() => onClickStoreHref(item.id, item.name)} className={`${styles.viewInMap} ${styles.detailsLink}`} href={`/${lang}_${market}/shops${item.url}`}>{t('tiendas.locator', 'see.details', '***See details')}</a>
        </div>)
      }
    </div>
  );
};

StoresGrid.propTypes = {
  stores: array,
  withMap: bool,
  handleViewInMap: oneOfType([ func, bool ]),
  selector: any,
  coloredStore: string,
  selectedStoreId: string,
  onMouseOverStore: func,
  onMouseOutStore: func,
  onClickStoreHref: func,
};

StoresGrid.defaultProps = {
  withMap: false,
  selector: null,
  onMouseOverStore: () => true,
  onMouseOutStore: () => true,
  onClickStoreHref: () => true,
};

export default StoresGrid;
